import { API_BASE_URL } from '../../config/api';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import SEO from '../component/SEO';
import './News.css';
import { FaFacebook, FaTwitter, FaLinkedin, FaWhatsapp, FaTelegram, FaEnvelope, FaLink } from 'react-icons/fa';

const NewsDetail = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [newsItem, setNewsItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchNewsItem = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/api/news/${id}/`, {
          headers: {
            'Accept-Language': i18n.language
          }
        });
        setNewsItem(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching news item:', err);
        setError(t('news.fetchError'));
      } finally {
        setLoading(false);
      }
    };

    fetchNewsItem();
  }, [id, i18n.language, t]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(i18n.language, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }).format(date);
  };

  // Функция для копирования ссылки
  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Функция для создания ссылок для шаринга
  const getShareLinks = () => {
    if (!newsItem) return {};
    
    const currentUrl = window.location.href;
    const title = encodeURIComponent(newsItem.title);
    const summary = encodeURIComponent(newsItem.summary || '');
    
    return {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${title}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`,
      whatsapp: `https://api.whatsapp.com/send?text=${title}%20${encodeURIComponent(currentUrl)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(currentUrl)}&text=${title}`,
      email: `mailto:?subject=${title}&body=${summary}%0A%0A${encodeURIComponent(currentUrl)}`
    };
  };

  // Компонент ShareButton для каждой платформы
  const ShareButton = ({ platform, icon, label, link }) => (
    <a 
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className={`share-button share-${platform}`}
      aria-label={label}
    >
      {icon}
      <span className="share-text">{label}</span>
    </a>
  );

  return (
    <div className="test_block">
      {loading ? (
        <div className="container">
          <div className="text-center my-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        </div>
      ) : error ? (
        <div className="container">
          <div className="alert alert-danger" role="alert">
            {error}
            <div className="mt-3">
              <Link to="/news" className="news-back-btn">
                {t('news.back')}
              </Link>
            </div>
          </div>
        </div>
      ) : !newsItem ? (
        <div className="container">
          <div className="news-detail-container">
            <div className="alert alert-info" role="alert">
              {t('news.itemNotFound')}
            </div>
            <Link to="/news" className="news-back-btn">
              {t('news.back')}
            </Link>
          </div>
        </div>
      ) : (
        <>
          <SEO
            title={`${newsItem.title} | ${t('seo.defaultTitle')}`}
            description={newsItem.summary}
          />
          
          <div className="container">
            <div className="news-detail">
              <Link to="/news" className="news-back-btn">
                {t('news.back')}
              </Link>
              
              <div className="news-detail-header">
                <div className="news-detail-date">{formatDate(newsItem.date)}</div>
                <h1 className="news-detail-title">{newsItem.title}</h1>
              </div>
              
              <div className="news-detail-image">
                <img src={newsItem.image} alt={newsItem.title} />
              </div>
              
              <div className="news-detail-content">
                <div dangerouslySetInnerHTML={{ __html: newsItem.content }} />
              </div>
              
              {newsItem.recommended_posts && newsItem.recommended_posts.length > 0 && (
                <div className="recommended-posts-section mt-5">
                  <h3 className="recommended-title">{t('news.recommendedPosts')}</h3>
                  <div className="row recommended-posts">
                    {newsItem.recommended_posts.map((post) => (
                      <div key={post.id} className="col-lg-4 col-md-6 col-sm-12 mb-4">
                        <div className="recommended-post-card">
                          <div className="recommended-post-image">
                            <Link to={`/news/${post.id}`}>
                              <img src={post.image} alt={post.title} />
                            </Link>
                          </div>
                          <div className="recommended-post-content">
                            <div className="recommended-post-date">{formatDate(post.date)}</div>
                            <h4 className="recommended-post-title">
                              <Link to={`/news/${post.id}`}>{post.title}</Link>
                            </h4>
                            <div className="recommended-post-summary" dangerouslySetInnerHTML={{ __html: post.summary }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Блок для шаринга новости */}
              <div className="news-share-container">
                <h4 className="share-title">{t('news.share')}</h4>
                <div className="share-buttons">
                  {newsItem && (
                    <>
                      <ShareButton 
                        platform="facebook" 
                        icon={<FaFacebook />} 
                        label={t('news.shareOn', { platform: 'Facebook' })}
                        link={getShareLinks().facebook}
                      />
                      <ShareButton 
                        platform="twitter" 
                        icon={<FaTwitter />} 
                        label={t('news.shareOn', { platform: 'Twitter' })}
                        link={getShareLinks().twitter}
                      />
                      <ShareButton 
                        platform="linkedin" 
                        icon={<FaLinkedin />} 
                        label={t('news.shareOn', { platform: 'LinkedIn' })}
                        link={getShareLinks().linkedin}
                      />
                      <ShareButton 
                        platform="whatsapp" 
                        icon={<FaWhatsapp />} 
                        label={t('news.shareOn', { platform: 'WhatsApp' })}
                        link={getShareLinks().whatsapp}
                      />
                      <ShareButton 
                        platform="telegram" 
                        icon={<FaTelegram />} 
                        label={t('news.shareOn', { platform: 'Telegram' })}
                        link={getShareLinks().telegram}
                      />
                      <ShareButton 
                        platform="email" 
                        icon={<FaEnvelope />} 
                        label={t('news.shareVia', { platform: 'Email' })}
                        link={getShareLinks().email}
                      />
                      <button 
                        className={`share-button share-copy ${copied ? 'copied' : ''}`}
                        onClick={copyToClipboard}
                        aria-label={t('news.copyLink')}
                      >
                        <FaLink />
                        <span className="share-text">
                          {copied ? t('news.linkCopied') : t('news.copyLink')}
                        </span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NewsDetail;
